import { Injectable, inject } from '@angular/core';
import { BaseService } from './base.service';
import { GlobalOptionsService } from './global-options.service';
import { IDocumentMetadata, IDocumentMetadataSQ, ISaveDocumentMetadata } from '../models/document.model';
import { ISR } from '../models/index.model';
import {
  Observable,
  catchError,
  concat,
  filter,
  forkJoin,
  last,
  lastValueFrom,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { UtilityService } from './utility.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentService extends BaseService {
  protected override baseURL: string = `v1/document`;
  constructor(public uS: UtilityService) {
    super();
  }

  private globalOptionsService = inject(GlobalOptionsService);

  downloadDocument = async (options: {
    docCode?: string | null;
    fileName?: string | null;
    filePath?: string | null;
  }) => {
    const { docCode, fileName, filePath } = options;

    if (docCode) {
      return lastValueFrom(this.apiS.getFile(this.baseURL + '/download/' + docCode)).then((r) =>
        this.uS.downloader(r, fileName || docCode),
      );
    } else if (fileName && filePath) {
      const fullUrl = await this.getDocumentFilePathUrl(fileName, filePath);
      const a = document.createElement('a');
      a.href = fullUrl;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return Promise.resolve();
    }
    return Promise.reject('Invalid parameters for downloadDocument');
  };

  deleteDocument = (docCode: string) => {
    return this.delete('/' + docCode);
  };

  deleteDocuments = (docCodes: string[]) => {
    return concat(...docCodes.map((docCode) => this.delete('/' + docCode)));
  };

  getDocumentFile = async (query: ISaveDocumentMetadata) => {
    return lastValueFrom(
      this.search({
        sortBy: 'createdOn',
        sortDirection: 'DESC',
        ...query,
      }).pipe(
        map((sr) => sr.content.sort2('createdOn', true, true)?.[0]),
        switchMap((document) => (document ? this.getDocumentURL(document.docCode) : of(null))),
      ),
    ).then((r) => {
      return r ? new File([r], '') : null;
    });
  };
  getDocumentLink = async (query: ISaveDocumentMetadata) =>
    this.getDocumentFile(query).then((r) => (r ? URL.createObjectURL(r) : null));
  getDocumentAsArrayBuffer = async (query: ISaveDocumentMetadata) =>
    this.getDocumentFile(query).then((r) =>
      r
        ? new Promise<ArrayBuffer>((res, rej) => {
            if (typeof FileReader !== 'undefined') {
              let reader = new FileReader();

              reader.onload = (e) => {
                res(e.target?.result as ArrayBuffer);
              };

              reader.readAsArrayBuffer(r);
            } else {
              rej(`FileReader was not found`);
            }
          })
        : null,
    );

  uploadDocument = (file: File, metadata: ISaveDocumentMetadata) => {
    // debugger;
    const fd = new FormData();

    let filename = file.name;
    if (metadata.fileName)
      filename = metadata.fileName.includes('.')
        ? metadata.fileName
        : `${metadata.fileName}.${file.name.split('.').lastItem()}`;
    fd.set('uploadParameters', JSON.stringify(metadata));
    fd.set('file', file, filename);
    return this.postFileFD<IDocumentMetadata>('', fd);
    // return this.postFile<IDocumentMetadata>('', { ...metadata, file: file });
  };

  updateDocument = (file: File, metadata: ISaveDocumentMetadata) => {
    // debugger;
    const fd = new FormData();

    let filename = file.name;
    if (metadata.fileName)
      filename = metadata.fileName.includes('.')
        ? metadata.fileName
        : `${metadata.fileName}.${file.name.split('.').lastItem()}`;
    fd.set('uploadParameters', JSON.stringify(metadata));
    fd.set('file', file, filename);
    return this.search(metadata).pipe(
      mergeMap((sr) =>
        sr.content.length > 0
          ? concat(...sr.content.map((doc) => this.deleteDocument(doc.docCode)))
          : of(null),
      ),
      last(),
      mergeMap(() => this.postFileFD<IDocumentMetadata>('', fd)),
    );

    // return this.postFileFD<IDocumentMetadata>('', fd);
    // return this.postFile<IDocumentMetadata>('', { ...metadata, file: file });
  };

  uploadDocuments = (documents: { file: File; meta: ISaveDocumentMetadata }[]) => {
    const res: {
      errorMessage?: string;
      successMeta?: IDocumentMetadata;
      reqMeta: ISaveDocumentMetadata;
    }[] = [];

    const obs = documents.length
      ? documents.map((d) =>
          this.uploadDocument(d.file, d.meta).pipe(
            map((r) => res.push({ successMeta: r, reqMeta: d.meta })),
            catchError((e) =>
              of(
                res.push({
                  errorMessage: e || 'An error occurred',
                  reqMeta: d.meta,
                }),
              ),
            ),
          ),
        )
      : [timer(100)];
    // debugger;
    return concat(...obs).pipe(map((r) => ({ result: res, errors: res.filter((x) => x.errorMessage) })));
  };

  updateDocuments = (documents: { file: File; meta: ISaveDocumentMetadata }[]) => {
    // debugger;
    const res: {
      errorMessage?: string;
      successMeta?: IDocumentMetadata;
      reqMeta: ISaveDocumentMetadata;
    }[] = [];

    const obs = documents.length
      ? documents.map((d) =>
          this.updateDocument(d.file, d.meta).pipe(
            map((r) => res.push({ successMeta: r, reqMeta: d.meta })),
            catchError((e) =>
              of(
                res.push({
                  errorMessage: e || 'An error occurred',
                  reqMeta: d.meta,
                }),
              ),
            ),
          ),
        )
      : [timer(100)];
    // debugger;
    return concat(...obs).pipe(map((r) => ({ result: res, errors: res.filter((x) => x.errorMessage) })));
  };

  search = (query: IDocumentMetadataSQ) => this.get<ISR<IDocumentMetadata>>('/search', query);

  getDocumentURL = (docCode: string) => {
    return this.apiS.getFile<ArrayBuffer>(this.baseURL + '/download/' + docCode);
  };

  getDocumentBlob = async (document: IDocumentMetadata & { filePath?: string }) => {
    if (document.docCode) {
      const response = await lastValueFrom(this.apiS.getFile<Blob>(this.baseURL + '/download/' + document.docCode));
      return response instanceof Blob
        ? response.slice(0, response.size, response.type || this.getMimeType(document.fileName))
        : new Blob([response], { type: this.getMimeType(document.fileName) });
    }

    if (document.fileName && document.filePath) {
      const response = await fetch(await this.getDocumentFilePathUrl(document.fileName, document.filePath));
      return response.blob();
    }

    throw 'Document does not exist';
  };

  private async getDocumentFilePathUrl(fileName: string, filePath: string) {
    const baseUrl = await this.globalOptionsService.getGlobalOption('documentBaseUrl');
    const sanitizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const sanitizedFilePath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
    const sanitizedFilePathEnd = sanitizedFilePath.endsWith('/')
      ? sanitizedFilePath.slice(0, -1)
      : sanitizedFilePath;
    let fullUrl = `${sanitizedBaseUrl}/${sanitizedFilePathEnd}/${fileName}`;
    if (!fullUrl.startsWith('http')) {
      fullUrl = this.apiS.baseURL + (fullUrl.startsWith('/') ? fullUrl : '/' + fullUrl);
    }
    return fullUrl;
  }

  private getMimeType(fileName?: string | null) {
    const extension = fileName?.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'txt':
        return 'text/plain';
      case 'csv':
        return 'text/csv';
      default:
        return 'application/octet-stream';
    }
  }
}
